import { AccessorsMutatorsDecorator } from '../../helpers';
import { TranslatableModel } from './translatable-model';
import { Property } from './property.model';
import { GuestsStatus } from './guests-status.model';
import { Country } from './country.model';
import { GeneralSettings } from './general-settings.model';
import { City } from './city.model';

/**
 * @class GuestsStatus
 * @property {number} id
 * @property {Property} property
 * @property {string} name
 */
export class Guest extends TranslatableModel {

    static get TITLE_MR() {
        return {
            key: 1,
            value: 'MR',
        };
    }

    static get TITLE_MRS() {
        return {
            key: 2,
            value: 'MRS',
        };
    }

    static get TITLE_MISS() {
        return {
            key: 3,
            value: 'MISS',
        };
    }

    static get TITLE_MS() {
        return {
            key: 4,
            value: 'MS',
        };
    }

    static get TITLE_DR() {
        return {
            key: 5,
            value: 'DR',
        };
    }

    static get TITLES() {
        return [
            this.TITLE_MR,
            this.TITLE_MRS,
            this.TITLE_MISS,
            this.TITLE_MS,
            this.TITLE_DR,
        ];
    }

    static get NATIONAL_TYPE() {
        return {
            key: 1,
            value: 'NATIONAL_ID',
        };
    }

    static get IQAMA_TYPE() {
        return {
            key: 2,
            value: 'IQAMA',
        };
    }

    static get PASSPORT_TYPE() {
        return {
            key: 3,
            value: 'PASSPORT',
        };
    }

    static get FAMILY_CARD_TYPE() {
        return {
            key: 4,
            value: 'FAMILY_CARD',
        };
    }

    static get GCC_TYPE() {
        return {
            key: 5,
            value: 'GCC_ID',
        };
    }

    static get CR_TYPE() {
        return {
            key: 6,
            value: 'CR',
        };
    }

    static get ID_TYPES() {

        return [
            this.NATIONAL_TYPE,
            this.IQAMA_TYPE,
            this.PASSPORT_TYPE,
            this.FAMILY_CARD_TYPE,
            this.GCC_TYPE,
            this.CR_TYPE,
        ];
    }

    static get INDIVIDUAL_GUEST() {

        return {
            key: 1,
            value: 'INDIVIDUAL_GUEST'
        };
    }

    static get CORPORATE_CUSTOMER() {

        return {
            key: 2,
            value: 'CORPORATE_CUSTOMER'
        };
    }

    static get CLASSIFICATIONS() {

        return [
            this.INDIVIDUAL_GUEST,
            this.CORPORATE_CUSTOMER
        ];
    }

    static get CITIZEN() {
        return {
            key: 1,
            value: 'CITIZEN'
        };
    }

    static get MUQEEM() {
        return {
            key: 2,
            value: 'RESIDENT'
        };
    }

    static get GCC_CITIZEN() {
        return {
            key: 3,
            value: 'GULF_CITIZEN'
        };
    }

    static get VISITOR() {
        return {
            key: 4,
            value: 'VISITOR'
        };
    }

    static get CUSTOMER_TYPE() {
        return [
            this.CITIZEN,
            this.MUQEEM,
            this.GCC_CITIZEN,
            this.VISITOR,
        ];
    }

    static get RELATION_SON() {

        return 1;
    }

    static get RELATION_DAUGHTER() {

        return 2;
    }

    static get RELATION_WIFE() {

        return 3;
    }

    static get RELATION_BROTHER() {

        return 4;
    }

    static get RELATION_SISTER() {

        return 5;
    }

    static get RELATION_FATHER() {

        return 6;
    }

    static get RELATION_MOTHER() {

        return 7;
    }

    static get RELATION_HUSBAND() {

        return 8;
    }

    static get RELATION_OTHER() {

        return 9;
    }

    fill(attributes = {}) {
        super.fill(attributes);

        if (angular.isString(this.expiry_date)) {

            this.expiry_date = new Date(this.expiry_date);
        }
    }

    nameBuild(format) {

        if (this.classification === this.constructor.CORPORATE_CUSTOMER.key) {

            return this.name;
        }

        const lastName = this.last_name ? this.last_name : '';

        const firstName = this.name ? this.name : '';

        if(firstName === '' && lastName === ''){

            return 'Child';
        }

        if (format == GeneralSettings.NAME_FIRST_FORMAT.key) {

            return `${firstName} ${lastName}`;

        } else {

            return `${lastName}, ${firstName}`;
        }
    }

    fullName() {

        return this.nameBuild(GeneralSettings.NAME_FIRST_FORMAT.key);
    }

    formatName(format) {

        return this.nameBuild(format || this.constructor.nameFormat);
    }

    formatTitle(value = this.title) {

        return ((this.constructor.TITLES || []).find(x => value === x.key) || {}).value;
    }

    formatCustomerType(value = this.customer_type) {

        return ((this.constructor.CUSTOMER_TYPE || []).find(x => value === x.key) || {}).value;
    }

    formatIdType(value = this.id_type) {

        return ((this.constructor.ID_TYPES || []).find(x => value === x.key) || {}).value;
    }

    /**
     * @param value
     * @returns {Country}
     */
    setCountry(value) {

        if(!value) value = {id: 0, name: '', code: '', name_ar: ''};

        return value instanceof Country ? value : new Country(value);
    }

    /**
     * @param value
     * @returns {Country}
     */
    setNationality(value) {

        if(!value) value = {id: 0, name: '', code: '', name_ar: ''};

        return value && (value instanceof Country ? value : new Country(value));
    }

    /**
     * @param value
     * @returns {City}
     */
    setCity(value) {

        return value && (value instanceof City ? value : new City(value));
    }

    /**
     * @param value
     * @returns {GuestsStatus}
     */
    setGuestStatus(value) {

        return value && (value instanceof GuestsStatus ? value : new GuestsStatus(value));
    }

    /**
     * @param {Object|Property} value
     * @returns {Property}
     */
    setProperty(value) {

        return value instanceof Property ? value : new Property(value);
    }

    applyDefaultCustomFields(defaultFields) {

        const additionalInformation = this.additional_information;
        const additionalFieldIds = additionalInformation.map(({id}) => id);

        const defaultCustomFields = defaultFields
            .filter(({id, apply_to}) => !additionalFieldIds.includes(id) && (apply_to === (1 + this.classification)))
            .map(customField => {

                customField.pivot = {
                    value: customField.selected_radio_option,
                };

                return customField;
            });

        this.additional_information = this.additional_information.concat(defaultCustomFields);
    }

    static get validationModel() {

        return {
            email: {
                label: 'EMAIL',
                messages: {
                    email: 'EMAIL_MUST_BE_IN_THE_FORMAT_EMAIL'
                }
            },
            commercial_registration_no: {
                label: 'COMMERCIAL_REGISTRATION_NO',
                commonLabel: 'Commercial Registration No.',
                messages: {
                    pattern: 'COMMERCIAL_REGISTRATION_NO_MUST_BE_NUMBERS_ONLY'
                }
            },
            district: {
                label: 'DISTRICT',
                commonLabel: 'District',
            },
            street: {
                label: 'STREET',
                commonLabel: 'Street',
            },
            building_number: {
                label: 'BUILDING_NUMBER',
                commonLabel: 'Building Number',
                messages: {
                    pattern: 'BUILDING_NUMBER_MUST_BE_NUMBERS_ONLY'
                }
            },
            company_postal_code: {
                label: 'POSTAL_CODE',
                commonLabel: 'Postal Code',
            }
        };
    }
}

AccessorsMutatorsDecorator(
    'id',
    'classification',
    'title',
    'name',
    'father_name',
    'grandfather',
    'last_name',
    'customer_type',
    'nationality',
    'nationality_id',
    'id_type',
    'id_number',
    'id_serial',
    'visa_number',
    'issue_place',
    'expiry_date',
    'address',
    'country',
    'country_id',
    'city',
    'city_id',
    'phone_code',
    'phone',
    'email',
    'work_place',
    'work_phone_code',
    'work_phone_number',
    'contact_person',
    'repeat',
    'property',
    'guest_status',
    'guest_status_id',
    'photo_file',
    'photo',
    'additional_information',
    'custom',
    'empty',
    'group',
    'reservation_id',
    'accommodations',
    'primary_accommodation',
    'is_child',
    'additional_guests',
    'replace_model',
    'guest_relation',
    'vat_number',
    'district',
    'street',
    'building_number',
    'company_postal_code',
)(Guest);



// WEBPACK FOOTER //
// ./src/pms-api/models/guests.model.js